import posthog from "posthog-js";

export const ANALYTICS_CONSENT_KEY = "homeswap.analyticsConsent";

export type AnalyticsConsent = "granted" | "denied" | "unset";

type AnalyticsMode = "none" | "anonymous" | "consented";

let initializedMode: AnalyticsMode = "none";

function getPostHogApiKey() {
  return window.env.REACT_APP_POSTHOG_KEY?.trim();
}

function getPostHogHost() {
  return window.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
}

export function hasPostHogConfigured() {
  return Boolean(getPostHogApiKey());
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (value === "granted" || value === "denied") {
      return value;
    }
  } catch (error) {
    return "unset";
  }

  return "unset";
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, "unset">) {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
  } catch (error) {
    return;
  }
}

export function initializePostHogIfConsented(
  consent: AnalyticsConsent = getAnalyticsConsent(),
) {
  const apiKey = getPostHogApiKey();
  if (!apiKey) {
    return false;
  }

  const targetMode: AnalyticsMode = consent === "granted" ? "consented" : "anonymous";
  if (initializedMode === targetMode) {
    return true;
  }

  posthog.init(apiKey, {
    api_host: getPostHogHost(),
    capture_pageview: false,
    autocapture: false,
    disable_session_recording: true,
    capture_pageleave: true,
    persistence: targetMode === "consented" ? "localStorage+cookie" : "memory",
  });

  initializedMode = targetMode;
  return true;
}

export function applyAnalyticsConsent(
  consent: AnalyticsConsent = getAnalyticsConsent(),
) {
  initializePostHogIfConsented(consent);
}

export function trackPostHogEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean | null>,
) {
  const initialized = initializePostHogIfConsented();
  if (!initialized || initializedMode !== "consented") {
    return false;
  }

  posthog.capture(eventName, properties || {});
  return true;
}

export function capturePostHogPageView(currentUrl: string): boolean {
  const initialized = initializePostHogIfConsented();
  if (!initialized || initializedMode === "none") {
    return false;
  }

  posthog.capture("$pageview", {
    $current_url: currentUrl,
  });

  return true;
}
